import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Github, CheckCircle2, AlertCircle, ExternalLink, Unplug, ArrowUpRight, LogIn } from 'lucide-react'
import Spinner from '../Spinner'
import type { RootState } from '../../store'
import { Link } from '../nav'
import type { GithubAppStatus } from '../../data/projectTypes'
import { useGetGithubAppStatusQuery, useUpdateProjectMutation } from '../../services/projectDataApi'

// The GitHub connect/disconnect walkthrough, rendered INLINE in the chat onboarding for a GitHub repo
// URL. It polls github-app-status, walks the user through installing / adding the App and disconnecting
// any project already connected to the repo, and calls onReady once the repo is connectable — at which
// point the parent advances to the GitHub testing questions. Compact styling reuses chat.css .rp-card*.

const POLL_MS = 5000
const INSTALL_URL = (slug: string) => `https://github.com/apps/${slug}/installations/new`

export default function GithubConnectChatCard({
  repoUrl,
  onReady,
  onSignIn,
  onBeforeInstall,
}: {
  repoUrl: string
  onReady: (info: { repoFullName: string; defaultBranch: string }) => void
  onSignIn: () => void
  /** Called just before leaving for github.com, so the caller can snapshot the conversation first. */
  onBeforeInstall?: () => void
}) {
  const [updateProject] = useUpdateProjectMutation()
  const user = useSelector((s: RootState) => s.auth.user)
  const [error, setError] = useState<string | null>(null)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  // Fire onReady exactly once per repo (polling keeps running until then, but must not refire).
  const readyFired = useRef(false)

  const statusQuery = useGetGithubAppStatusQuery({ repo: repoUrl }, {
    skip: !user,
    pollingInterval: POLL_MS,
    skipPollingIfUnfocused: true,
  })
  const status = statusQuery.data as GithubAppStatus | undefined
  const connectable = !!status && status.installed && !status.existingProject
  const blocked = !!status && status.appConfigured === false
  const statusError = statusQuery.error ? 'Failed to check the GitHub App' : null
  const displayError = error ?? statusError

  // Advance the parent once the repo is connectable (App installed, no project already connected).
  useEffect(() => {
    if (connectable && status && !readyFired.current) {
      readyFired.current = true
      onReady({ repoFullName: status.repoFullName ?? repoUrl, defaultBranch: status.defaultBranch || 'main' })
    }
  }, [connectable, status, repoUrl, onReady])

  const slug = status?.slug || (import.meta.env.VITE_GITHUB_APP_SLUG as string | undefined) || 'replay-qa'

  async function disconnect(projectId: string) {
    // Keep confirmDisconnect set: the render switches to a dedicated "Disconnecting…" waiting state
    // while this runs (clearing confirm here would flash the plain "Disconnect" button back instead).
    setDisconnecting(true)
    setError(null)
    try {
      await updateProject({ id: projectId, data: { action: 'update-settings', github: null } }).unwrap()
      await statusQuery.refetch()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to disconnect the existing project')
    } finally {
      setDisconnecting(false)
    }
  }

  // Signed out — the status endpoint needs auth, so ask the user to sign in first.
  if (!user) {
    return (
      <div className="rp-card" data-testid="chat-github" data-status="signed-out">
        <div className="rp-card__head">
          <Github size={15} className="rp-card__icon" aria-hidden />
          <span className="rp-card__title">Connect your GitHub repository</span>
        </div>
        <p className="rp-card__text">
          Sign in to connect <code>{repoUrl}</code> through the Replay QA GitHub App and set up QA on its
          deployments and pull requests.
        </p>
        <div className="rp-card__actions">
          <button type="button" className="rp-card__copy" onClick={onSignIn} data-testid="chat-github-signin" data-analytics-event="chat-github-signin">
            <LogIn size={13} aria-hidden /> Sign in to connect GitHub
          </button>
        </div>
      </div>
    )
  }

  // The App isn't set up server-side in this environment — be honest, there's nothing to install.
  if (blocked) {
    return (
      <div className="rp-card rp-card--warn" data-testid="chat-github" data-status="unconfigured">
        <div className="rp-card__head">
          <AlertCircle size={15} className="rp-card__icon" aria-hidden />
          <span className="rp-card__title">GitHub App unavailable</span>
        </div>
        <p className="rp-card__text">
          The Replay QA GitHub App isn&apos;t enabled in this environment yet — contact an administrator.
        </p>
      </div>
    )
  }

  // Still loading the first status.
  if (!status) {
    return (
      <div className="rp-card" data-testid="chat-github" data-status="checking">
        <div className="rp-card__head">
          <Spinner size={15} className="rp-card__icon" />
          <span className="rp-card__title">Checking the GitHub App…</span>
        </div>
        {displayError && <p className="rp-card__text">{displayError}</p>}
      </div>
    )
  }

  // Connected + no existing project — ready. (onReady has advanced the parent; show a brief confirm.)
  if (connectable) {
    return (
      <div className="rp-card rp-card--ready" data-testid="chat-github" data-status="ready">
        <CheckCircle2 size={15} className="rp-card__icon rp-card__icon--ok" aria-hidden />
        <div className="rp-card__main">
          <span className="rp-card__title">GitHub connected</span>
          <span className="rp-card__text">The Replay QA GitHub App can reach <code>{status.repoFullName}</code>.</span>
        </div>
      </div>
    )
  }

  // Installed, but the repo is already connected to a project — offer to disconnect or open it.
  if (status.installed && status.existingProject) {
    const proj = status.existingProject
    return (
      <div className="rp-card" data-testid="chat-github" data-status="existing-project">
        <div className="rp-card__head">
          <Github size={15} className="rp-card__icon" aria-hidden />
          <span className="rp-card__title">Repository already connected</span>
        </div>
        {disconnecting ? (
          <p className="rp-card__text" data-testid="chat-github-disconnecting">
            <Spinner size={13} className="rp-card__icon" /> Disconnecting <code>{status.repoFullName}</code> from <strong>{proj.name || proj.id}</strong>…
          </p>
        ) : (
          <>
            <p className="rp-card__text">
              <code>{status.repoFullName}</code> is already connected to the project <strong>{proj.name || proj.id}</strong>.
              Disconnect it to set up a new project here, or open the existing one.
            </p>
            <div className="rp-card__actions">
              {confirmDisconnect ? (
                <>
                  <button type="button" className="rp-card__copy" onClick={() => disconnect(proj.id)} data-testid="chat-github-disconnect-confirm" data-analytics-event="chat-github-disconnect-confirm">
                    <Unplug size={13} aria-hidden /> Confirm disconnect
                  </button>
                  <button type="button" className="rp-card__copy" onClick={() => setConfirmDisconnect(false)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button type="button" className="rp-card__copy" onClick={() => setConfirmDisconnect(true)} data-testid="chat-github-disconnect" data-analytics-event="chat-github-disconnect">
                  <Unplug size={13} aria-hidden /> Disconnect
                </button>
              )}
              <Link className="rp-card__copy" to={`/projects/${proj.id}/overview`} data-testid="chat-github-open-existing" data-analytics-event="chat-github-open-existing">
                <ArrowUpRight size={13} aria-hidden /> Open project
              </Link>
            </div>
          </>
        )}
        {displayError && <p className="rp-card__text">{displayError}</p>}
      </div>
    )
  }

  // Not installed. If the App is already on the repo's owner, the user just adds this repo; otherwise
  // it's a fresh install. Either way this leaves for github.com in the SAME tab: the App's Setup URL
  // brings the browser back to the new-project screen, which restores the conversation (hence
  // onBeforeInstall) and this card then detects the installation on its next poll. Opening a new tab
  // instead would land the return in a tab that has no conversation to come back to.
  const addRepo = !!status.ownerInstalled
  return (
    <div className="rp-card" data-testid="chat-github" data-status={addRepo ? 'add-repo' : 'not-installed'}>
      <div className="rp-card__head">
        <Github size={15} className="rp-card__icon" aria-hidden />
        <span className="rp-card__title">{addRepo ? 'Add this repository' : 'Connect the Replay QA GitHub App'}</span>
      </div>
      <p className="rp-card__text">
        {addRepo
          ? <>The Replay QA GitHub App is installed, but doesn&apos;t cover <code>{status.repoFullName}</code> yet. Add this repository to your installation — I&apos;ll continue automatically once it&apos;s connected.</>
          : <>Install the Replay QA GitHub App on <code>{status.repoFullName}</code> so QA can run on its deployments and pull requests. I&apos;ll continue automatically once it&apos;s connected.</>}
      </p>
      <div className="rp-card__actions">
        <a className="rp-card__copy" href={INSTALL_URL(slug)} onClick={onBeforeInstall} data-testid="chat-github-install" data-analytics-event="chat-github-install">
          <Github size={13} aria-hidden /> {addRepo ? 'Add this repository' : 'Set up the GitHub App'} <ExternalLink size={12} aria-hidden />
        </a>
      </div>
      <div className="rp-card__waiting">
        <Spinner size={13} className="rp-card__icon" />
        <span>Waiting for the connection… detected automatically.</span>
      </div>
      {displayError && <p className="rp-card__text">{displayError}</p>}
    </div>
  )
}
